import { ClientEvents, ReferralShareLocation, ShareType } from 'common/events/ClientEvents';
import { formatGetValue, formatTotalValue } from 'common/ReferTextUtilities';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useLocation } from 'src/shared/hooks/useLocation';
import { useShare } from 'src/shared/hooks/useShare';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Routes } from 'src/shared/Routes';
import { getReferralUrl } from 'src/shared/utils/PromoUtilities';
import Button, { ButtonType } from 'web-common/src/shared/design-system/components/Button';
import { ReactComponent as Delivery } from '../assets/delivery.svg';
import { ReactComponent as Swipe } from '../assets/swipe.svg';

const FormattedCurrency: React.FC<{ text: string }> = ({ text }) => (
  <span className='font-juniper-bay text-2xl leading-5 text-mint-500 md:text-4xl'>{text}</span>
);

const SHARE_COPY = 'Share link';
const COPIED_COPY = 'Link copied';

export const WSSReferralBanner = () => {
  const tracker = useTracker();
  const [currentUser] = useCurrentUser();
  const referralCode = currentUser?.referralCode;

  const giveText = referralCode ? formatTotalValue(referralCode) : '';
  const getText = referralCode ? formatGetValue(referralCode) : '';
  const referralShareMessage =
    giveText && getText
      ? `Get ${giveText} off! You'll receive ${getText} off your first 2 orders using my referral link.`
      : '';

  const code = referralCode?.code ?? '';
  const referralLink = getReferralUrl({ code });
  const [copyText, setCopyText] = useState<React.ReactNode>(SHARE_COPY);
  const location = useLocation();

  const onShare = (type: ShareType) => {
    tracker.track(ClientEvents.REFERRAL_CODE_SHARED, {
      code,
      type,
      location:
        location.pathname === Routes.CONSUMER_MEAL_PLANS_MANAGE
          ? ReferralShareLocation.MANAGE_MEAL_PLAN
          : ReferralShareLocation.UPCOMING_MEAL_PLAN_ORDERS,
    });
    if (type !== ShareType.NATIVE) {
      setCopyText(COPIED_COPY);
      setTimeout(() => {
        setCopyText(SHARE_COPY);
      }, 2000);
    }
  };

  const { share } = useShare({
    url: referralLink,
    onShare,
    onError: () => null,
    shareMessage: referralShareMessage,
  });

  if (isEmpty(referralCode)) {
    return null;
  }

  return (
    <div
      className='relative flex min-h-44 w-full cursor-pointer items-center overflow-hidden rounded-lg bg-mint-200 px-6 shadow-navigation-drop-top md:gap-8'
      onClick={share}
      role='button'>
      <div className='max-md:w-9/10 z-10'>
        <p className='mb-2 text-caption-700-caps text-eggplant'>Earn unlimited credits!</p>
        <p className='mb-3 w-full text-h5 font-bold leading-7 text-eggplant max-md:text-h5-bold'>
          Share <FormattedCurrency text={giveText} /> in Shef credits, get <FormattedCurrency text={getText} /> off your
          next order.
        </p>
        <Button buttonType={ButtonType.TextLink} align={'center'} onClick={share}>
          {copyText}
        </Button>
      </div>
      <Swipe className='absolute right-0 top-8 z-0 h-full w-40 md:w-60' />
      <Delivery className='absolute bottom-0 right-2 size-20' />
    </div>
  );
};
